import { FormControl } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Slide from '@material-ui/core/Slide';
import TextField from '@material-ui/core/TextField';
import WarningIcon from '@material-ui/icons/Warning';
import { useFormik } from 'formik';
import React from 'react';
import { LYNKED_RED } from 'src/consts/colors';
import EnqueueAlert from 'src/contexts/EnqueueAlertContext';
import MutateCreateUser from 'src/hooks/MutateCreateUser';
import active_lang from 'src/langs/jp_en';
import * as Yup from 'yup';
import ShowDebugJson from '../ShowDebugJson';
import Autocomplete from '@material-ui/lab/Autocomplete';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const genders = [
  {
    id: 0,
    value: 'None',
  },
  {
    id: 1,
    value: 'Male',
  },
  {
    id: 2,
    value: 'Female',
  },
];

export default function AddNewUserDialog({
  open,
  setOpen,
  handleListAllUsers,
}) {
  let mutationCreateUser = MutateCreateUser();
  let { showEnqueue } = React.useContext(EnqueueAlert);
  const [gender, setGender] = React.useState(genders[0]);

  const handleClose = () => {
    setOpen(false);
    handleListAllUsers();
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      name: '',
      user_password: '',
      gender: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(active_lang.ADMIN_NOT_A_VALID_EMAIL),
      name: Yup.string(),
      user_password: Yup.string().min(6, active_lang.ADMIN_NOT_ENOUGH_LONG),
    }),
    onSubmit: values => {
      showEnqueue.warning('try create user');
      mutationCreateUser
        .mutateAsync({
          email: values.email,
          name: values.name,
          password: values.user_password,
          gender: values.gender?.id ?? 0,
        })
        .then(res => {
          if (res.result != 'create user ok') throw Error(res.reason.message);

          showEnqueue.success(`user added ${JSON.stringify(res, null, 2)}`);
          formik.resetForm();
          handleClose();
        })
        .catch(err => {
          showEnqueue.error(`error during create user ${err}`);
          console.error('mutationCreateUser', err);
        });
    },
  });

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        maxWidth={'lg'}
        keepMounted
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            formik.handleSubmit(e);
          }}
        >
          <DialogTitle id="alert-dialog-slide-title">
            <Grid container>
              <Grid item md={12} style={{ textAlign: 'center' }}>
                <Box>{active_lang.ADMIN_ADD_NEW_USER}</Box>
              </Grid>
            </Grid>
          </DialogTitle>

          <DialogContent>
            <Box mt="1rem">
              <FormControl>
                <TextField
                  id="email"
                  {...formik.getFieldProps('email')}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.ADMIN_USER_EMAIL}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.email && formik.errors.email ? (
                  <Box
                    style={{
                      color: LYNKED_RED,
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      paddingLeft: '0.25rem',
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                    }}
                  >
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.email}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl>
                <TextField
                  id="name"
                  {...formik.getFieldProps('name')}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.ADMIN_USER_NAME}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.name && formik.errors.name ? (
                  <Box
                    style={{
                      color: LYNKED_RED,
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      paddingLeft: '0.25rem',
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                    }}
                  >
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.name}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl>
                <TextField
                  id="user_password"
                  {...formik.getFieldProps('user_password')}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.ADMIN_USER_PASSWORD}
                  variant="outlined"
                  fullWidth
                  required
                />
                {formik.touched.user_password && formik.errors.user_password ? (
                  <Box
                    style={{
                      color: LYNKED_RED,
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      paddingLeft: '0.25rem',
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                    }}
                  >
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.user_password}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <Autocomplete
                  id="genders"
                  {...formik.getFieldProps('gender')}
                  options={genders}
                  value={gender}
                  getOptionLabel={option => option.value}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="GenderSelect"
                      variant="outlined"
                    />
                  )}
                  disableClearable
                  onChange={(event, newValue) => {
                    setGender(newValue);
                    formik.setFieldValue('gender', newValue);
                  }}
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              onClick={() => {
                formik.resetForm();
                handleClose();
              }}
              color="primary"
            >
              {active_lang.ADMIN_USER_UPDATE_CANCEL}
            </Button>
            <Button
              // disabled={formik.isSubmitting || !formik.dirty}
              type="submit"
              color="primary"
              variant="contained"
            >
              {active_lang.ADMIN_USER_UPDATE_OK}
            </Button>
          </DialogActions>
          <ShowDebugJson>{formik}</ShowDebugJson>
        </form>
      </Dialog>
    </>
  );
}
